import { useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
} from "reactstrap";
import {
  filterIssues,
  getSeverityClass,
  getSeverityRowClass,
  normalizeIssueReport,
} from "../../../constants/issueReportUtils";
import {
  exportIssueReportExcel,
  exportIssueReportPdf,
} from "../../../utils/issueReportExport";

const BomIssueReportModal = ({
  isOpen,
  toggle,
  issueReport,
  bomName,
  onIssueClick,
}) => {
  const [severityFilter, setSeverityFilter] = useState("all");
  const normalized = useMemo(
    () => normalizeIssueReport(issueReport),
    [issueReport]
  );
  const filteredIssues = useMemo(
    () => filterIssues(normalized.issues, severityFilter),
    [normalized.issues, severityFilter]
  );

  const handleExportExcel = () => {
    exportIssueReportExcel(normalized, bomName ? `BOM-Issues-${bomName}` : "BOM-Issues");
  };

  const handleExportPdf = () => {
    exportIssueReportPdf(normalized, {
      bomName,
      fileName: bomName ? `BOM-Issues-${bomName}` : "BOM-Issues",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="bom-issue-report-modal"
      contentClassName="bom-issue-report-modal__content"
    >
      <ModalHeader className="bom-issue-report-modal__header">
        <span>BOM Issue Report</span>
        <button
          type="button"
          className="close bom-issue-report-modal__close"
          onClick={toggle}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <div className="small text-muted">
              {normalized.generated_at
                ? `Generated ${normalized.generated_at}`
                : "Not generated yet"}
            </div>
            <div>
              <strong>{normalized.summary.total}</strong> issues
              {normalized.summary.error > 0 && (
                <span className="text-danger ml-2">
                  ({normalized.summary.error} errors)
                </span>
              )}
              {normalized.summary.warning > 0 && (
                <span className="text-warning ml-2">
                  ({normalized.summary.warning} warnings)
                </span>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center">
            <span className="mr-2 small">Filter:</span>
            <Input
              type="select"
              bsSize="sm"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              style={{ width: 140 }}
            >
              <option value="all">All</option>
              <option value="error">Errors</option>
              <option value="warning">Warnings</option>
              <option value="info">Info</option>
            </Input>
          </div>
        </div>

        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Item #</th>
                <th>Part Number / Manufacturer</th>
                <th>Ref Des</th>
                <th>Issue Type</th>
                <th>Description</th>
                <th>Recommended Action</th>
                <th>Severity</th>
              </tr>
            </thead>
            <tbody>
              {filteredIssues.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No issues detected.
                  </td>
                </tr>
              ) : (() => {
                // Group consecutive issues by part number + item number
                const groups = [];
                filteredIssues.forEach((issue) => {
                  const groupKey = `${issue.item_number}||${issue.manufacturer_part_number || ""}`;
                  const last = groups[groups.length - 1];
                  if (last && last.groupKey === groupKey) {
                    last.issues.push(issue);
                  } else {
                    groups.push({ groupKey, issues: [issue] });
                  }
                });

                return groups.map((group) => {
                  const first = group.issues[0];
                  return group.issues.map((issue, idx) => (
                    <tr
                      key={issue.id || `${issue.item_number}-${issue.issue_type}-${idx}`}
                      className={onIssueClick ? "cursor-pointer" : getSeverityRowClass(issue.severity)}
                      onClick={() => onIssueClick && onIssueClick(issue)}
                    >
                      {idx === 0 && (
                        <>
                          <td rowSpan={group.issues.length} style={{ verticalAlign: "middle" }}>
                            {first.item_number}
                          </td>
                          <td rowSpan={group.issues.length} style={{ verticalAlign: "middle" }}>
                            <div className="font-weight-bold">{first.manufacturer_part_number || "—"}</div>
                            {first.manufacturer && (
                              <div className="text-muted small">{first.manufacturer}</div>
                            )}
                          </td>
                        </>
                      )}
                      <td>{issue.reference_designator || "—"}</td>
                      <td>{issue.issue_label || issue.issue_type}</td>
                      <td>{issue.description}</td>
                      <td>{issue.recommended_action}</td>
                      <td className={getSeverityClass(issue.severity)}>
                        {issue.severity
                          ? issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)
                          : ""}
                      </td>
                    </tr>
                  ));
                });
              })()}
            </tbody>
          </Table>
        </div>
      </ModalBody>
      <ModalFooter className="bom-issue-report-modal__footer">
        <Button color="secondary" outline onClick={handleExportExcel}>
          Export Excel
        </Button>
        <Button color="secondary" outline onClick={handleExportPdf}>
          Export PDF
        </Button>
        <Button color="primary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BomIssueReportModal;
